import React, { useState, useEffect, useRef } from "react"
import {
  Box,
  Flex,
  Heading,
  Text,
  Select,
  Switch,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Image,
  AspectRatio,
  Stack,
  IconButton,
  Grid,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  HStack,
  VStack,
  Badge,
  Center,
  Spacer,
  RadioGroup,
  Radio,
  Checkbox,
  CheckboxGroup,
  Tag,
  TagLabel,
  TagRightIcon,
  Tooltip,
  Spinner,
} from "@chakra-ui/react"
import { Formik, Form, Field } from "formik"
import * as Yup from "yup"
import axios from "axios"
import {
  Edit,
  Trash2,
  Plus,
  Upload,
  SearchIcon,
  Clock,
  Send,
  CheckCircle,
  XCircle,
  AlertCircle,
} from "lucide-react"
import FormEditor from "../../components/form/formField/FormEditor"

const API_BASE = `${process.env.REACT_APP_BASE_URL}/admin`

// Validation schema for new campaign
const newCampaignSchema = Yup.object().shape({
  name: Yup.string().required("Campaign name is required").trim(),
})

const scheduleSchema = Yup.object().shape({
  mode: Yup.string().oneOf(["once", "daily", "weekly", "monthly"]).required(),
  startDate: Yup.date().when("mode", {
    is: "once",
    then: (schema) => schema.required("Start date is required"),
  }),
  endDate: Yup.date().when("mode", {
    is: "once",
    then: (schema) =>
      schema
        .min(Yup.ref("startDate"), "End date must be after start date")
        .required("End date is required"),
  }),
  activeHours: Yup.object().shape({
    start: Yup.string().matches(
      /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
      "Invalid time"
    ),
    end: Yup.string().matches(
      /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
      "Invalid time"
    ),
  }),
  daysOfWeek: Yup.array().when("mode", {
    is: "weekly",
    then: (schema) => schema.min(1, "Select at least one day"),
  }),
  daysOfMonth: Yup.array().when("mode", {
    is: "monthly",
    then: (schema) => schema.min(1, "Select at least one day"),
  }),
})

// Updated advert schema to support { id, status } structure
const advertSchema = Yup.object().shape({
  image: Yup.string().required("Image is required"),
  landscapeImage: Yup.string().required("Landscape image is required"),
  tnc: Yup.string().nullable(),
  details: Yup.string().nullable(),
  ctaText: Yup.string().required("CTA text is required").trim(),
  ctaColor: Yup.string().default("#007bff"),
  textColor: Yup.string().default("#ffffff"),
  action: Yup.string()
    .oneOf(["single_restaurant", "restaurant_list"])
    .required(),
  restaurant: Yup.mixed().when("action", {
    is: "single_restaurant",
    then: (schema) =>
      schema.test(
        "restaurant-id-required",
        "Select a restaurant",
        (value: any) => !!value && !!value.id
      ),
  }),
  restaurants: Yup.array().when("action", {
    is: "restaurant_list",
    then: (schema) =>
      schema
        .of(
          Yup.object().shape({
            id: Yup.string().required(),
            status: Yup.mixed().nullable(),
          })
        )
        .min(1, "Select at least one restaurant"),
  }),
  listTitle: Yup.string().when("action", {
    is: "restaurant_list",
    then: (schema) => schema.required("List title is required"),
  }),
})

const ScheduleForm = ({ values, setFieldValue }: any) => {
  const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]

  return (
    <Stack spacing={5} mt={4} p={4} bg="gray.50" borderRadius="lg">
      <FormControl>
        <FormLabel>Schedule Mode</FormLabel>
        <RadioGroup
          value={values.schedule.mode}
          onChange={(v) =>
            setFieldValue((p: any) => ({
              ...p,
              schedule: {
                mode: v,
                startDate: "",
                endDate: "",
                activeHours: { start: "", end: "" },
                daysOfWeek: [],
                daysOfMonth: [],
              },
            }))
          }
        >
          <HStack spacing={6}>
            <Radio value="once">Every Day</Radio>
            <Radio value="weekly">Every Week</Radio>
            <Radio value="monthly">Every Month</Radio>
          </HStack>
        </RadioGroup>
      </FormControl>

      {values.schedule.mode === "once" && (
        <>
          <HStack>
            <FormControl>
              <FormLabel>Start Date</FormLabel>
              <Input
                type="date"
                value={values.schedule.startDate?.slice(0, 10) || ""}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e: any) =>
                  setFieldValue((p: any) => ({
                    ...p,
                    schedule: {
                      ...p.schedule,
                      startDate: e.target.value,
                    },
                  }))
                }
              />
            </FormControl>
            <FormControl>
              <FormLabel>End Date</FormLabel>
              <Input
                type="date"
                value={values.schedule.endDate?.slice(0, 10) || ""}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e: any) =>
                  setFieldValue((p: any) => ({
                    ...p,
                    schedule: {
                      ...p.schedule,
                      endDate: e.target.value,
                    },
                  }))
                }
              />
            </FormControl>
          </HStack>
        </>
      )}

      {values.schedule.mode === "weekly" && (
        <FormControl>
          <FormLabel>Days of Week</FormLabel>
          <CheckboxGroup
            value={(values.schedule.daysOfWeek || []).map(String)}
            onChange={(v) =>
              setFieldValue((p: any) => ({
                ...p,
                schedule: {
                  ...p.schedule,
                  daysOfWeek: v,
                },
              }))
            }
          >
            <HStack spacing={4}>
              {daysOfWeek.map((day, i) => (
                <Checkbox key={i} value={(i + 1).toString()}>
                  {day}
                </Checkbox>
              ))}
            </HStack>
          </CheckboxGroup>
        </FormControl>
      )}

      {values.schedule.mode === "monthly" && (
        <FormControl>
          <FormLabel>Days of Month</FormLabel>
          <HStack spacing={2} flexWrap="wrap">
            {[...Array(31)].map((_, i) => (
              <Checkbox
                key={i + 1}
                value={(i + 1).toString()}
                isChecked={values.schedule.daysOfMonth?.includes(i + 1)}
                onChange={(e: any) => {
                  const checked = e.target.checked
                  const day = i + 1
                  const current = values.schedule.daysOfMonth || []
                  setFieldValue((p: any) => ({
                    ...p,
                    schedule: {
                      ...p.schedule,
                      daysOfMonth: checked
                        ? [...current, day]
                        : current.filter((d: number) => d !== day),
                    },
                  }))
                }}
              >
                {i + 1}
              </Checkbox>
            ))}
            <Checkbox
              value="-1"
              isChecked={values.schedule.daysOfMonth?.includes(-1)}
              onChange={(e: any) => {
                const checked = e.target.checked
                const current = values.schedule.daysOfMonth || []

                setFieldValue((p: any) => ({
                  ...p,
                  schedule: {
                    ...p.schedule,
                    daysOfMonth: checked
                      ? [...current.filter((d: number) => d !== -1), -1]
                      : current.filter((d: number) => d !== -1),
                  },
                }))
              }}
            >
              Last Day
            </Checkbox>
          </HStack>
        </FormControl>
      )}

      <HStack>
        <FormControl>
          <FormLabel>Active From (optional)</FormLabel>
          <Input
            type="time"
            value={values.schedule.activeHours?.start || ""}
            onChange={(e: any) => {
              const value = e.target.value

              setFieldValue((p: any) => ({
                ...p,
                schedule: {
                  ...p.schedule,
                  activeHours: {
                    ...p.schedule.activeHours,
                    start: value,
                  },
                },
              }))
            }}
          />
        </FormControl>
        <FormControl>
          <FormLabel>To (optional)</FormLabel>
          <Input
            type="time"
            value={values.schedule.activeHours?.end || ""}
            onChange={(e: any) => {
              const value = e.target.value
              setFieldValue((p: any) => ({
                ...p,
                schedule: {
                  ...p.schedule,
                  activeHours: {
                    ...p.schedule.activeHours,
                    end: value,
                  },
                },
              }))
            }}
          />
        </FormControl>
      </HStack>

      {values.schedule.activeHours?.start &&
        values.schedule.activeHours?.end && (
          <Badge colorScheme="blue" w="fit-content">
            <Clock size={14} /> Active {values.schedule.activeHours.start} –{" "}
            {values.schedule.activeHours.end}
          </Badge>
        )}
    </Stack>
  )
}

/**
 * Advert Card Component
 */
const AdvertCard = ({ advert, onEdit, onDelete }: any) => {
  return (
    <Box
      borderWidth="1px"
      borderRadius="lg"
      overflow="hidden"
      bg="white"
      boxShadow="md"
      position="relative"
      _hover={{
        boxShadow: "lg",
        transform: "translateY(-2px)",
        transition: "all 0.15s ease-out",
      }}
    >
      <AspectRatio ratio={2 / 3}>
        <Image
          src={advert.image}
          alt="Advert"
          objectFit="cover"
          fallbackSrc="https://via.placeholder.com/400x600?text=No+Image"
        />
      </AspectRatio>

      <Box
        bg={advert.ctaColor}
        color={advert.textColor}
        p={3}
        textAlign="center"
      >
        <Text fontWeight="bold" fontSize="sm">
          {advert.ctaText}
        </Text>
      </Box>

      <VStack
        position="absolute"
        top={2}
        right={2}
        spacing={1}
        align="flex-end"
      >
        <IconButton
          size="xs"
          colorScheme="blue"
          icon={<Edit size={14} />}
          onClick={onEdit}
          aria-label="Edit advert"
          borderRadius="full"
        />
        <IconButton
          size="xs"
          colorScheme="red"
          icon={<Trash2 size={14} />}
          onClick={onDelete}
          aria-label="Delete advert"
          borderRadius="full"
        />
      </VStack>
    </Box>
  )
}

/**
 * NEW: Participating Restaurant Item with Status & Actions
 */
const ParticipatingRestaurantItem = ({
  restaurant,
  onRemove,
  onResend,
  onForceAdd,
}: any) => {
  const statusConfig: any = {
    accepted: { color: "green", icon: CheckCircle, label: "Accepted" },
    manual: { color: "green", icon: CheckCircle, label: "Manual" },
    pending: { color: "yellow", icon: Clock, label: "Pending" },
    rejected: { color: "red", icon: XCircle, label: "Rejected" },
    expired: { color: "gray", icon: AlertCircle, label: "Expired" },
    null: { color: "blue", icon: null, label: "Added (Manual)" },
  }

  const config =
    statusConfig[restaurant.status ?? "pending"] || statusConfig.pending

  return (
    <HStack
      p={3}
      bg="white"
      borderRadius="md"
      boxShadow="sm"
      justify="space-between"
      borderLeft="4px solid"
      borderColor={`${config.color}.500`}
    >
      <HStack>
        <Text fontWeight="medium">{restaurant.name}</Text>
        <Tag size="sm" colorScheme={config.color} variant="subtle">
          {config.icon && <TagRightIcon as={config.icon} />}
          <TagLabel>{config.label}</TagLabel>
        </Tag>
      </HStack>
      <HStack>
        {(restaurant.status == "invited" ||
          restaurant.status == "rejected" ||
          restaurant.status == "expired") && (
            <>
              <Tooltip label="Resend Invite">
                <IconButton
                  size="xs"
                  icon={<Send size={14} />}
                  onClick={onResend}
                  aria-label="Resend"
                />
              </Tooltip>
              <Tooltip label="Force Add (Accept)">
                <IconButton
                  size="xs"
                  colorScheme="green"
                  icon={<CheckCircle size={14} />}
                  onClick={onForceAdd}
                  aria-label="Force Add"
                />
              </Tooltip>
            </>
          )}
        {(restaurant.status == "accepted" || restaurant.status == "manual") && (
          <Tooltip label="Remove from Campaign">
            <IconButton
              size="xs"
              colorScheme="red"
              icon={<Trash2 size={14} />}
              onClick={onRemove}
              aria-label="Remove"
            />
          </Tooltip>
        )}
      </HStack>
    </HStack>
  )
}

/**
 * Advert Form Component
 */
const AdvertForm = ({
  isOpen,
  onClose,
  onSubmit,
  initialValues,
  toast,
  restaurants,
  disabledRestaurantIds = [],
}: any) => {
  const [imagePreview, setImagePreview] = useState(initialValues?.image || null)
  const [landscapeImagePreview, setLandscapeImagePreview] = useState(
    initialValues?.landscapeImage || null
  )

  useEffect(() => {
    setImagePreview(initialValues?.image || null)
    setLandscapeImagePreview(initialValues?.landscapeImage || null)
  }, [initialValues])

  const [searchQuery, setSearchQuery] = useState("")
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const [isUploadingImage, setIsUploadingImage] = useState(false) // ← New state
  const [isUploadingLandscape, setIsUploadingLandscape] = useState(false)

  const handleImageChange = async (e: any, setFieldValue: any) => {
    try {
      const file = e.target.files[0]
      if (file) {
        setIsUploadingImage(true) // Start loading

        const formData = new FormData()
        formData.append("file", file)
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/upload/media`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        )
        const previewUrl = `${process.env.REACT_APP_IMAGE_BASE_URL}/${response.data.data.url}`
        setImagePreview(previewUrl)
        setFieldValue("image", previewUrl)
      }
    } catch (error: any) {
      console.error("Error uploading image:", error)
      toast({
        title: "Error uploading image",
        description: error.message,
        status: "error",
        duration: 3000,
      })
    } finally {
      setIsUploadingImage(false) // End loading
    }
  }

  const handleLandscapeImageChange = async (e: any, setFieldValue: any) => {
    try {
      const file = e.target.files[0]
      if (file) {
        setIsUploadingLandscape(true)

        const formData = new FormData()
        formData.append("file", file)
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/upload/media`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        )
        const previewUrl = `${process.env.REACT_APP_IMAGE_BASE_URL}/${response.data.data.url}`
        setLandscapeImagePreview(previewUrl)
        setFieldValue("landscapeImage", previewUrl)
      }
    } catch (error: any) {
      console.error("Error uploading landscape image:", error)
      toast({
        title: "Error uploading image",
        description: error.message,
        status: "error",
        duration: 3000,
      })
    } finally {
      setIsUploadingLandscape(false)
    }
  }

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen)
    if (!isDropdownOpen) {
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 0)
    }
  }

  // Filter restaurants based on search query
  const filteredRestaurants = restaurants.filter((restaurant: any) =>
    restaurant.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false)
        setSearchQuery("")
      }
    }

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isDropdownOpen])

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="2xl" h="90vh">
        <ModalHeader bg="red.50" borderTopRadius="2xl">
          {initialValues ? "Edit Advert" : "Add Advert"}
        </ModalHeader>
        <ModalCloseButton />

        <Formik
          enableReinitialize
          initialValues={
            initialValues || {
              image: "",
              landscapeImage: "",
              tnc: "",
              details: "",
              ctaText: "",
              ctaColor: "#007bff",
              textColor: "#ffffff",
              action: "single_restaurant",
              restaurant: { id: "", status: "pending" },
              restaurants: [],
              listTitle: "",
            }
          }
          validationSchema={advertSchema}
          onSubmit={(values, actions) => {
            if (!values.image) {
              actions.setFieldError("image", "Image is required")
              actions.setSubmitting(false)
              return
            }
            if (!values.landscapeImage) {
              actions.setFieldError(
                "landscapeImage",
                "Landscape image is required"
              )
              actions.setSubmitting(false)
              return
            }
            onSubmit(values)
            actions.setSubmitting(false)
            onClose()
          }}
        >
          {({ values, errors, touched, setFieldValue, isSubmitting }: any) => (
            <Form
              style={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                overflow: "hidden",
              }}
            >
              <ModalBody overflowY="auto" flex={1}>
                <Stack spacing={5}>
                  {/* Image Upload */}
                  {/* @ts-ignore */}
                  <FormControl isInvalid={errors.image && touched.image}>
                    <FormLabel>Advert Image (2:3 ratio)</FormLabel>

                    <Box>
                      <Box
                        borderWidth="2px"
                        borderStyle="dashed"
                        borderColor={errors.image ? "red.300" : "gray.300"}
                        borderRadius="lg"
                        p={4}
                        textAlign="center"
                        cursor="pointer"
                        _hover={{
                          borderColor: "blue.400",
                          bg: "blue.50",
                        }}
                        onClick={() =>
                          document.getElementById("advert-image-input")?.click()
                        }
                      >
                        <VStack spacing={3}>
                          {isUploadingImage ? (
                            <>
                              <Spinner size="lg" color="blue.500" />
                              <Text fontSize="sm" fontWeight="medium">
                                Uploading image...
                              </Text>
                            </>
                          ) : (
                            <>
                              <Upload size={22} />
                              <Text fontSize="sm" fontWeight="medium">
                                Upload Advert Image
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                Supported types: .png, .jpg, .jpeg
                              </Text>
                            </>
                          )}
                        </VStack>
                      </Box>

                      <Input
                        id="advert-image-input"
                        type="file"
                        accept="image/*"
                        display="none"
                        onChange={(e) => handleImageChange(e, setFieldValue)}
                      />

                      {errors.image && touched.image && (
                        <Text color="red.500" fontSize="sm" mt={2}>
                          {errors.image}
                        </Text>
                      )}
                    </Box>

                    {imagePreview && (
                      <Box mt={3} maxW="180px">
                        <AspectRatio ratio={2 / 3}>
                          <Image
                            src={imagePreview}
                            alt="Preview"
                            objectFit="cover"
                            borderRadius="md"
                          />
                        </AspectRatio>
                      </Box>
                    )}
                  </FormControl>

                  {/* Landscape Image Upload */}
                  {/* @ts-ignore */}
                  <FormControl
                    isInvalid={errors.landscapeImage && touched.landscapeImage}
                  >
                    <FormLabel>Landscape Image (3:2 ratio)</FormLabel>

                    <Box>
                      <Box
                        borderWidth="2px"
                        borderStyle="dashed"
                        borderColor={
                          errors.landscapeImage ? "red.300" : "gray.300"
                        }
                        borderRadius="lg"
                        p={4}
                        textAlign="center"
                        cursor="pointer"
                        _hover={{
                          borderColor: "blue.400",
                          bg: "blue.50",
                        }}
                        onClick={() =>
                          document
                            .getElementById("advert-landscape-image-input")
                            ?.click()
                        }
                      >
                        <VStack spacing={3}>
                          {isUploadingLandscape ? (
                            <>
                              <Spinner size="lg" color="blue.500" />
                              <Text fontSize="sm" fontWeight="medium">
                                Uploading image...
                              </Text>
                            </>
                          ) : (
                            <>
                              <Upload size={22} />
                              <Text fontSize="sm" fontWeight="medium">
                                Upload Landscape Image
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                Supported types: .png, .jpg, .jpeg
                              </Text>
                            </>
                          )}
                        </VStack>
                      </Box>

                      <Input
                        id="advert-landscape-image-input"
                        type="file"
                        accept="image/*"
                        display="none"
                        onChange={(e) =>
                          handleLandscapeImageChange(e, setFieldValue)
                        }
                      />

                      {errors.landscapeImage && touched.landscapeImage && (
                        <Text color="red.500" fontSize="sm" mt={2}>
                          {errors.landscapeImage}
                        </Text>
                      )}
                    </Box>

                    {landscapeImagePreview && (
                      <Box mt={3} maxW="270px">
                        <AspectRatio ratio={3 / 2}>
                          <Image
                            src={landscapeImagePreview}
                            alt="Preview"
                            objectFit="cover"
                            borderRadius="md"
                          />
                        </AspectRatio>
                      </Box>
                    )}
                  </FormControl>

                  {/* CTA Action */}
                  <FormControl>
                    <FormLabel>CTA Action</FormLabel>
                    <RadioGroup
                      value={values.action}
                      onChange={(v) => {
                        setFieldValue("action", v)
                        if (v === "single_restaurant") {
                          setFieldValue("restaurants", [])
                          setFieldValue("listTitle", "")
                        } else {
                          setFieldValue("restaurant", {
                            id: "",
                            status: "pending",
                          })
                        }
                      }}
                      isDisabled={
                        !!initialValues &&
                        ((values.restaurant &&
                          values.restaurant.status !== null &&
                          values.restaurant.status !== "pending") ||
                          (values.restaurants &&
                            values.restaurants.some(
                              (r: any) =>
                                r.status !== null && r.status !== "pending"
                            )))
                      }
                    >
                      <HStack spacing={6}>
                        <Radio value="single_restaurant">
                          Go to Restaurant
                        </Radio>
                        <Radio value="restaurant_list">
                          Show Restaurant List
                        </Radio>
                      </HStack>
                    </RadioGroup>
                  </FormControl>

                  {values.action === "single_restaurant" && (
                    <FormControl
                      isInvalid={!!errors.restaurant && !!touched.restaurant}
                    >
                      <FormLabel>Select Restaurant</FormLabel>
                      <Select
                        placeholder="Choose restaurant"
                        value={values.restaurant?.id || ""}
                        onChange={(e) =>
                          setFieldValue("restaurant", {
                            id: e.target.value,
                            status: "pending",
                          })
                        }
                        isDisabled={
                          values.restaurant &&
                          values.restaurant.status !== null &&
                          values.restaurant.status !== "pending"
                        }
                      >
                        {restaurants.map((r: any) => (
                          <option
                            key={r._id}
                            value={r._id}
                            disabled={disabledRestaurantIds.includes(r._id)}
                          >
                            {r.name}{" "}
                            {disabledRestaurantIds.includes(r._id) &&
                              "(Already Added)"}
                          </option>
                        ))}
                      </Select>
                      {errors.restaurant && touched.restaurant && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {typeof errors.restaurant === "string"
                            ? errors.restaurant
                            : "Select a restaurant"}
                        </Text>
                      )}
                    </FormControl>
                  )}

                  {values.action === "restaurant_list" && (
                    <>
                      <FormControl
                        isInvalid={!!errors.listTitle && touched.listTitle}
                      >
                        <FormLabel>List Title</FormLabel>
                        <Input
                          placeholder="e.g., Best Seafood in Town"
                          value={values.listTitle || ""}
                          onChange={(e) =>
                            setFieldValue("listTitle", e.target.value)
                          }
                        />
                        {errors.listTitle && touched.listTitle && (
                          <Text color="red.500" fontSize="sm" mt={1}>
                            {errors.listTitle}
                          </Text>
                        )}
                      </FormControl>

                      <FormControl
                        isInvalid={!!errors.restaurants && touched.restaurants}
                      >
                        <FormLabel>Select Restaurants</FormLabel>

                        {/* Custom Searchable Dropdown */}
                        <Box position="relative" ref={dropdownRef}>
                          <Box
                            onClick={toggleDropdown}
                            borderWidth="1px"
                            borderColor="gray.300"
                            borderRadius="md"
                            px={4}
                            py={2}
                            cursor="pointer"
                            bg="white"
                            _hover={{ borderColor: "gray.400" }}
                          >
                            <Text
                              color={
                                values.restaurants?.length > 0
                                  ? "black"
                                  : "gray.500"
                              }
                            >
                              {values.restaurants?.length > 0
                                ? `${values.restaurants.length} restaurant${values.restaurants.length > 1 ? "s" : ""
                                } selected`
                                : "Select restaurants"}
                            </Text>
                          </Box>

                          {isDropdownOpen && (
                            <Box
                              position="absolute"
                              top="calc(100% + 4px)"
                              left={0}
                              width="100%"
                              bg="white"
                              boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
                              borderRadius="md"
                              zIndex={1000}
                              maxH="300px"
                              overflowY="auto"
                            >
                              <Box
                                p={2}
                                position="sticky"
                                top={0}
                                bg="white"
                                borderBottom="1px solid"
                                borderColor="gray.200"
                                zIndex={1}
                              >
                                <InputGroup size="sm">
                                  <InputLeftElement pointerEvents="none">
                                    <SearchIcon size={16} />
                                  </InputLeftElement>
                                  <Input
                                    ref={searchInputRef}
                                    placeholder="Search restaurants..."
                                    value={searchQuery}
                                    onChange={(e) =>
                                      setSearchQuery(e.target.value)
                                    }
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.300"
                                    _focus={{
                                      outline: "none",
                                      borderColor: "blue.500",
                                      boxShadow: "0 0 0 1px #3182ce",
                                    }}
                                  />
                                </InputGroup>
                              </Box>

                              <Box>
                                {filteredRestaurants.length > 0 ? (
                                  filteredRestaurants.map((r: any) => {
                                    const isSelected = values.restaurants?.some(
                                      (item: any) => item.id === r._id
                                    )
                                    const isDisabled =
                                      disabledRestaurantIds.includes(r._id)

                                    return (
                                      <Flex
                                        key={r._id}
                                        alignItems="center"
                                        px={3}
                                        py={2}
                                        cursor={
                                          isDisabled ? "not-allowed" : "pointer"
                                        }
                                        _hover={{
                                          bg: isDisabled ? "white" : "gray.100",
                                        }}
                                        opacity={isDisabled ? 0.5 : 1}
                                        onMouseDown={(e) => {
                                          e.preventDefault()
                                          if (isDisabled) return

                                          const current =
                                            values.restaurants || []
                                          const selected = current.some(
                                            (item: any) => item.id === r._id
                                          )

                                          setFieldValue(
                                            "restaurants",
                                            selected
                                              ? current.filter(
                                                (item: any) =>
                                                  item.id !== r._id
                                              )
                                              : [
                                                ...current,
                                                {
                                                  id: r._id,
                                                  status: "pending",
                                                },
                                              ]
                                          )
                                          searchInputRef.current?.focus()
                                        }}
                                      >
                                        <Checkbox
                                          isChecked={isSelected}
                                          mr={2}
                                          pointerEvents="none"
                                        />
                                        <Text fontSize="sm">{r.name}</Text>
                                        {isDisabled && (
                                          <Text
                                            fontSize="xs"
                                            color="gray.500"
                                            ml={2}
                                          >
                                            (Already Added)
                                          </Text>
                                        )}
                                      </Flex>
                                    )
                                  })
                                ) : (
                                  <Box px={3} py={2}>
                                    <Text fontSize="sm" color="gray.500">
                                      No restaurants found
                                    </Text>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          )}
                        </Box>

                        {errors.restaurants && touched.restaurants && (
                          <Text color="red.500" fontSize="sm" mt={1}>
                            {typeof errors.restaurants === "string"
                              ? errors.restaurants
                              : "Select at least one restaurant"}
                          </Text>
                        )}
                      </FormControl>
                    </>
                  )}

                  {/* CTA Text */}
                  {/* @ts-ignore */}
                  <FormControl isInvalid={errors.ctaText && touched.ctaText}>
                    <FormLabel>CTA Text</FormLabel>
                    <Field
                      as={Input}
                      name="ctaText"
                      placeholder="e.g., Order Now"
                    />
                    {errors.ctaText && touched.ctaText && (
                      <Text color="red.500" fontSize="sm" mt={1}>
                        {errors.ctaText}
                      </Text>
                    )}
                  </FormControl>

                  {/* CTA Background Color */}
                  <FormControl>
                    <FormLabel>CTA Background Color</FormLabel>
                    <HStack spacing={3}>
                      <Input
                        type="color"
                        value={values.ctaColor}
                        onChange={(e) =>
                          setFieldValue("ctaColor", e.target.value)
                        }
                        w="72px"
                        h="40px"
                        p={1}
                        cursor="pointer"
                        borderRadius="md"
                      />
                      <Input
                        value={values.ctaColor}
                        onChange={(e) =>
                          setFieldValue("ctaColor", e.target.value)
                        }
                        placeholder="#007bff"
                      />
                    </HStack>
                  </FormControl>

                  {/* CTA Text Color */}
                  <FormControl>
                    <FormLabel>CTA Text Color</FormLabel>
                    <HStack spacing={3}>
                      <Input
                        type="color"
                        value={values.textColor}
                        onChange={(e) =>
                          setFieldValue("textColor", e.target.value)
                        }
                        w="72px"
                        h="40px"
                        p={1}
                        cursor="pointer"
                        borderRadius="md"
                      />
                      <Input
                        value={values.textColor}
                        onChange={(e) =>
                          setFieldValue("textColor", e.target.value)
                        }
                        placeholder="#ffffff"
                      />
                    </HStack>
                  </FormControl>

                  {/* Preview */}
                  {imagePreview && values.ctaText && (
                    <Box>
                      <FormLabel>Preview</FormLabel>
                      <Box maxW="180px" borderRadius="lg" overflow="hidden">
                        <AspectRatio ratio={2 / 3}>
                          <Image
                            src={imagePreview}
                            alt="Preview"
                            objectFit="cover"
                          />
                        </AspectRatio>
                        <Box
                          bg={values.ctaColor}
                          color={values.textColor}
                          p={2}
                          textAlign="center"
                        >
                          <Text fontWeight="bold" fontSize="sm">
                            {values.ctaText}
                          </Text>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {/* Terms & Conditions */}
                  <FormEditor
                    name="Terms & Conditions"
                    fieldName="tnc"
                    required={false}
                    // @ts-ignore
                    error={errors.tnc && touched.tnc ? errors.tnc : undefined}
                  />

                  {/* Details */}
                  <FormEditor
                    name="Details"
                    fieldName="details"
                    required={false}
                    // @ts-ignore
                    error={
                      errors.details && touched.details
                        ? errors.details
                        : undefined
                    }
                  />
                </Stack>
              </ModalBody>

              <ModalFooter bg="gray.50" borderBottomRadius="2xl">
                <Button variant="ghost" mr={3} onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  type="submit"
                  disabled={isUploadingImage || isUploadingLandscape}
                  isLoading={isSubmitting}
                  px={8}
                >
                  {initialValues ? "Update Advert" : "Add Advert"}
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

/**
 * New Campaign Modal
 */
const NewCampaignModal = ({ isOpen, onClose, onCreate }: any) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent borderRadius="2xl">
        <ModalHeader bg="red.50" borderTopRadius="2xl">
          Create New Campaign
        </ModalHeader>
        <ModalCloseButton />

        <Formik
          initialValues={{ name: "" }}
          validationSchema={newCampaignSchema}
          onSubmit={(values, actions) => {
            onCreate(values.name)
            actions.setSubmitting(false)
            onClose()
          }}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form>
              <ModalBody>
                {/* @ts-ignore */}
                <FormControl isInvalid={errors.name && touched.name}>
                  <FormLabel>Campaign Name</FormLabel>
                  <Field
                    as={Input}
                    name="name"
                    placeholder="Enter campaign name"
                  />
                  {errors.name && touched.name && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.name}
                    </Text>
                  )}
                </FormControl>
              </ModalBody>

              <ModalFooter bg="gray.50" borderBottomRadius="2xl">
                <Button variant="ghost" mr={3} onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  type="submit"
                  isLoading={isSubmitting}
                  px={8}
                >
                  Create Campaign
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

/**
 * Main Advert Campaign Admin Page
 */
const AdvertCampaign = () => {
  const [campaigns, setCampaigns] = useState<any[]>([])
  const [restaurants, setRestaurants] = useState<any[]>([])
  const [selectedCampaign, setSelectedCampaign] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editingAdvert, setEditingAdvert] = useState<any>(null)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)

  const toast = useToast()
  const {
    isOpen: isAdvertFormOpen,
    onOpen: onAdvertFormOpen,
    onClose: onAdvertFormClose,
  } = useDisclosure()
  const {
    isOpen: isNewCampaignOpen,
    onOpen: onNewCampaignOpen,
    onClose: onNewCampaignClose,
  } = useDisclosure()

  // Fetch campaigns on mount
  useEffect(() => {
    fetchCampaigns()
    fetchRestaurants()
  }, [])

  const normalizeAdvert = (ad: any) => {
    const action = ad.action || "single_restaurant"

    // Normalize single restaurant
    let restaurantObj: any = { id: "", status: "pending" }
    if (ad.restaurant) {
      if (typeof ad.restaurant === "string") {
        restaurantObj = { id: ad.restaurant, status: "pending" }
      } else if (typeof ad.restaurant === "object") {
        restaurantObj = {
          id: ad.restaurant.id || ad.restaurant._id || "",
          status:
            ad.restaurant.status === undefined
              ? "pending"
              : ad.restaurant.status,
        }
      }
    }

    // Normalize restaurant list
    let restaurantsArr: any[] = []
    if (Array.isArray(ad.restaurants)) {
      restaurantsArr = ad.restaurants.map((r: any) => {
        if (typeof r === "string") {
          return { id: r, status: "pending" }
        }
        return {
          id: r.id || r._id || "",
          status: r.status === undefined ? "pending" : r.status,
        }
      })
    }

    return {
      ...ad,
      action,
      restaurant: restaurantObj,
      restaurants: restaurantsArr,
    }
  }

  const fetchCampaigns = async () => {
    setLoading(true)
    try {
      const response = await axios.get(`${API_BASE}/advert-campaigns`)
      const raw = response.data.data || []

      const normalizedCampaigns = raw.map((c: any) => ({
        ...c,
        adverts: Array.isArray(c.adverts)
          ? c.adverts.map((ad: any) => normalizeAdvert(ad))
          : [],
      }))

      setCampaigns(normalizedCampaigns)

      setSelectedCampaign((prev: any) => {
        if (!prev) return prev
        const updated = normalizedCampaigns.find((c: any) => c._id === prev._id)
        return updated ? { ...updated } : prev
      })

      toast({
        title: "Campaigns loaded",
        status: "success",
        duration: 2000,
      })
    } catch (error: any) {
      console.error("Error fetching campaigns:", error)
      toast({
        title: "Error loading campaigns",
        description: error.message,
        status: "error",
        duration: 3000,
      })
    } finally {
      setLoading(false)
    }
  }

  const fetchRestaurants = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/admin/restaurant`
      )
      setRestaurants(res.data.data)

      toast({
        title: "Restaurants loaded",
        status: "success",
        duration: 2000,
      })
    } catch (error: any) {
      console.error("Error fetching campaigns:", error)
      toast({
        title: "Error loading campaigns",
        description: error.message,
        status: "error",
        duration: 3000,
      })
    }
  }

  const handleCampaignSelect = (e: any) => {
    const value = e.target.value

    if (value === "__new__") {
      onNewCampaignOpen()
      return
    }

    if (value === "") {
      setSelectedCampaign(null)
      return
    }

    const campaign: any = campaigns.find((c: any) => c._id === value)
    setSelectedCampaign(campaign ? { ...campaign } : null)
  }

  const handleCreateCampaign = (name: any) => {
    const newCampaign: any = {
      _id: `temp-${Date.now()}`,
      name,
      adverts: [],
      isActive: false,
      isConsentRequired: false,
      schedule: {
        mode: "once",
        startDate: "",
        endDate: "",
        activeHours: { start: "", end: "" },
        daysOfWeek: [],
        daysOfMonth: [],
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    setCampaigns([...campaigns, newCampaign])
    setSelectedCampaign(newCampaign)
    toast({
      title: "Campaign created",
      description: "Add adverts and click Save to persist",
      status: "info",
      duration: 3000,
    })
  }

  const handleActiveToggle = async (checked: any) => {
    if (!selectedCampaign) return

    const updated = { ...selectedCampaign, isActive: checked }
    setSelectedCampaign(updated)

    if (!String(selectedCampaign._id).startsWith("temp-")) {
      try {
        await axios.patch(
          `${API_BASE}/advert-campaigns/${selectedCampaign._id}`,
          {
            isActive: checked,
          }
        )
        toast({
          title: `Campaign ${checked ? "activated" : "deactivated"}`,
          status: "success",
          duration: 2000,
        })
      } catch (error) {
        console.error("Error updating active status:", error)
        toast({
          title: "Error updating status",
          description: "Changes will be saved when you click Save Campaign",
          status: "warning",
          duration: 3000,
        })
      }
    }
  }

  const handleConsentToggle = (checked: boolean) => {
    if (!selectedCampaign) return
    // Only allow changing if it's a new (temp) campaign
    if (!String(selectedCampaign._id).startsWith("temp-")) return

    setSelectedCampaign({ ...selectedCampaign, isConsentRequired: checked })
  }

  const handleAddAdvert = () => {
    setEditingAdvert(null)
    setEditingIndex(null)
    onAdvertFormOpen()
  }

  const handleEditAdvert = (advert: any, index: any) => {
    setEditingAdvert(advert)
    setEditingIndex(index)
    onAdvertFormOpen()
  }

  const handleAdvertSubmit = (values: any) => {
    if (!selectedCampaign) return

    const updatedAdverts = [...(selectedCampaign.adverts || [])]

    if (editingIndex !== null) {
      updatedAdverts[editingIndex] = values
      toast({
        title: "Advert updated",
        status: "success",
        duration: 2000,
      })
    } else {
      updatedAdverts.push(values)
      toast({
        title: "Advert added",
        status: "success",
        duration: 2000,
      })
    }

    setSelectedCampaign({
      ...selectedCampaign,
      adverts: updatedAdverts,
    })
  }

  const handleDeleteAdvert = (index: any) => {
    if (!selectedCampaign) return

    const updatedAdverts = (selectedCampaign.adverts || []).filter(
      (_: any, i: number) => i !== index
    )
    setSelectedCampaign({
      ...selectedCampaign,
      adverts: updatedAdverts,
    })

    toast({
      title: "Advert deleted",
      status: "info",
      duration: 2000,
    })
  }

  const handleSaveCampaign = async () => {
    if (!selectedCampaign) return

    if (!selectedCampaign.adverts || selectedCampaign.adverts.length === 0) {
      toast({
        title: "Validation Error",
        description: "An advert campaign must contain at least one advert",
        status: "error",
        duration: 4000,
      })
      return
    }

    setSaving(true)

    try {
      const isNew = String(selectedCampaign._id).startsWith("temp-")

      const payload = {
        name: selectedCampaign.name,
        adverts: selectedCampaign.adverts.map((p: any) => {
          const advert = { ...p }

          if (advert.action === "restaurant_list") {
            delete advert.restaurant
          }
          if (advert.action === "single_restaurant") {
            delete advert.restaurants
            delete advert.listTitle
          }

          return advert
        }),
        isActive: selectedCampaign.isActive,
        isConsentRequired: selectedCampaign.isConsentRequired,
        schedule: Object.fromEntries(
          Object.entries(selectedCampaign.schedule || {}).filter(
            ([_, value]) => {
              if (value === "" || value === null || value === undefined)
                return false
              if (Array.isArray(value) && value.length === 0) return false
              return true
            }
          )
        ),
      }

      if (isNew) {
        const response = await axios.post(
          `${API_BASE}/advert-campaigns`,
          payload
        )

        const savedCampaign = response.data.data
        setCampaigns(
          campaigns.map((c) =>
            c._id === selectedCampaign._id ? savedCampaign : c
          )
        )
        setSelectedCampaign(savedCampaign)

        toast({
          title: "Campaign created successfully",
          status: "success",
          duration: 3000,
        })
      } else {
        const response = await axios.patch(
          `${API_BASE}/advert-campaigns/${selectedCampaign._id}`,
          payload
        )

        const updatedCampaign = response.data.data
        setCampaigns(
          campaigns.map((c) =>
            c._id === selectedCampaign._id ? updatedCampaign : c
          )
        )
        setSelectedCampaign(updatedCampaign)

        toast({
          title: "Campaign updated successfully",
          status: "success",
          duration: 3000,
        })
      }

      fetchCampaigns()
    } catch (error: any) {
      console.error("Error saving campaign:", error)
      toast({
        title: "Error saving campaign",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 4000,
      })
    } finally {
      setSaving(false)
    }
  }

  // Extract all restaurants with status from current advert (first advert drives participation)
  const getAllParticipatingRestaurants = () => {
    if (!selectedCampaign || !selectedCampaign.adverts?.[0]) return []
    const advert = selectedCampaign.adverts[0]
    const list: any[] = []

    if (advert.action === "single_restaurant" && advert.restaurant?.id) {
      const rest = restaurants.find((r) => r._id === advert.restaurant.id)
      if (rest) list.push({ ...rest, status: advert.restaurant.status })
    } else if (
      advert.action === "restaurant_list" &&
      advert.restaurants?.length > 0
    ) {
      advert.restaurants.forEach((r: any) => {
        const rest = restaurants.find((re) => re._id === r.id)
        if (rest) list.push({ ...rest, status: r.status })
      })
    }
    return list
  }

  const participatingRestaurants = getAllParticipatingRestaurants()
  const disabledRestaurantIds = participatingRestaurants.map((r: any) => r._id)

  const handleResendInvite = async (restaurantId: string) => {
    try {
      await axios.post(`${API_BASE}/advert-campaigns/resend-mail`, {
        restaurant: restaurantId,
        campaign: selectedCampaign._id,
      })
      toast({ title: "Invite resent!", status: "success" })
      fetchCampaigns()
    } catch (error: any) {
      console.error("Error resent campaign:", error)
      toast({
        title: "Error resent campaign",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 4000,
      })
    }
  }

  const handleForceAdd = async (restaurantId: string) => {
    try {
      await axios.post(`${API_BASE}/advert-campaigns/force-add`, {
        restaurant: restaurantId,
        campaign: selectedCampaign._id,
      })
      fetchCampaigns()
      toast({ title: "Restaurant force-added!", status: "success" })
    } catch (error: any) {
      console.error("Error force add:", error)
      toast({
        title: "Error force add",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 4000,
      })
    }
  }

  const handleRemoveRestaurant = async (restaurantId: string) => {
    try {
      await axios.post(`${API_BASE}/advert-campaigns/remove-restaurant`, {
        restaurant: restaurantId,
        campaign: selectedCampaign._id,
      })
      fetchCampaigns()
      toast({ title: "removed!", status: "success" })
    } catch (error: any) {
      console.error("Error remove campaign:", error)
      toast({
        title: "Error remove campaign",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 4000,
      })
    }
  }

  return (
    <div
      style={{
        width: "96vw",
        maxWidth: "96vw",
        maxHeight: "86vh",
        height: "86vh",
        top: "8vh ",
        left: "4vw",
        overflowY: "auto",
        position: "relative",
        backgroundColor: "#EEEEEE",
        padding: "30px",
      }}
    >
      <Flex gap={6} maxW="1440px" mx="auto" align="flex-start">
        {/* Left Column - Campaign Selector */}
        <Box
          flex="0 0 300px"
          bg="white"
          p={6}
          borderRadius="2xl"
          boxShadow="md"
        >
          <VStack spacing={6} align="stretch">
            <Heading size="md">Advert Campaigns</Heading>

            <FormControl>
              <FormLabel>Campaign</FormLabel>
              <Select
                value={selectedCampaign?._id || ""}
                onChange={handleCampaignSelect}
                placeholder={loading ? "Loading..." : "Select campaign"}
              >
                {campaigns.map((campaign: any) => (
                  <option key={campaign._id} value={campaign._id}>
                    {campaign.name}
                  </option>
                ))}
                <option value="__new__">+ Add Campaign</option>
              </Select>
            </FormControl>

            {selectedCampaign && (
              <>
                <FormControl
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <FormLabel mb={0}>Active</FormLabel>
                  <Switch
                    isChecked={selectedCampaign.isActive}
                    onChange={(e) => handleActiveToggle(e.target.checked)}
                    colorScheme="green"
                    size="lg"
                  />
                </FormControl>

                <FormControl
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mt={2}
                >
                  <FormLabel mb={0}>Consent Required</FormLabel>
                  <Switch
                    isChecked={!!selectedCampaign.isConsentRequired}
                    onChange={(e) => handleConsentToggle(e.target.checked)}
                    colorScheme="purple"
                    size="lg"
                    isDisabled={
                      !String(selectedCampaign._id).startsWith("temp-")
                    }
                  />
                </FormControl>

                <Box pt={4} borderTopWidth="1px">
                  <VStack spacing={3} align="stretch">
                    <HStack justify="space-between">
                      <Text fontSize="sm" fontWeight="medium">
                        Adverts
                      </Text>
                      <Badge colorScheme="blue">
                        {selectedCampaign.adverts.length}
                      </Badge>
                    </HStack>
                    <Badge
                      colorScheme={selectedCampaign.isActive ? "green" : "gray"}
                      borderRadius="full"
                      py={1}
                      textAlign="center"
                    >
                      {selectedCampaign.isActive
                        ? "CURRENTLY ACTIVE"
                        : "INACTIVE"}
                    </Badge>
                  </VStack>
                </Box>

                <ScheduleForm
                  values={selectedCampaign}
                  setFieldValue={setSelectedCampaign}
                />
              </>
            )}
          </VStack>
        </Box>

        {/* Right Column */}
        <Box flex="1">
          <Box
            bg="white"
            borderRadius="2xl"
            boxShadow="md"
            p={6}
            borderTop="4px solid"
            borderTopColor="red.400"
          >
            <Flex justify="space-between" align="center" mb={4}>
              <Heading size="lg">
                {selectedCampaign ? selectedCampaign.name : "Advert Campaigns"}
              </Heading>

              {selectedCampaign && (
                <Button
                  colorScheme="blue"
                  onClick={handleSaveCampaign}
                  isLoading={saving}
                  loadingText="Saving..."
                  size="md"
                  px={8}
                  borderRadius="full"
                >
                  Save Campaign
                </Button>
              )}
            </Flex>

            {!selectedCampaign ? (
              <Center h="300px" color="gray.500">
                <Text>Select or create a campaign to manage adverts</Text>
              </Center>
            ) : (
              <Stack spacing={6}>
                {/* Participating & Invited List */}
                {participatingRestaurants.length > 0 && (
                  <Box>
                    <Heading size="sm" mb={3}>
                      Participating & Invited Restaurants
                    </Heading>
                    <VStack spacing={3} align="stretch">
                      {participatingRestaurants
                        .filter((r) => r.status !== "pending")
                        .map((rest: any) => (
                          <ParticipatingRestaurantItem
                            key={rest._id}
                            restaurant={rest}
                            onResend={() => handleResendInvite(rest._id)}
                            onForceAdd={() => handleForceAdd(rest._id)}
                            onRemove={() => handleRemoveRestaurant(rest._id)}
                          />
                        ))}
                    </VStack>
                  </Box>
                )}

                {/* Red-bordered Adverts Container */}
                <Box
                  border="2px solid"
                  borderColor="red.300"
                  borderRadius="xl"
                  p={6}
                  bg="red.50"
                  minH="320px"
                >
                  <Stack spacing={4}>
                    <HStack justify="space-between" align="center">
                      <Text fontSize="md" fontWeight="semibold">
                        Adverts in this campaign
                      </Text>
                      <Button
                        leftIcon={<Plus size={16} />}
                        colorScheme="green"
                        onClick={handleAddAdvert}
                        size="sm"
                        borderRadius="full"
                      >
                        Add Advert
                      </Button>
                    </HStack>

                    {selectedCampaign.adverts.length === 0 ? (
                      <Flex
                        align="center"
                        justify="center"
                        h="220px"
                        direction="column"
                        color="gray.500"
                        borderWidth="2px"
                        borderStyle="dashed"
                        borderColor="gray.300"
                        borderRadius="lg"
                        bg="white"
                      >
                        <Text fontSize="md" mb={2}>
                          No adverts yet.
                        </Text>
                        <Text fontSize="sm">
                          Click <b>Add Advert</b> to create your first advert.
                        </Text>
                      </Flex>
                    ) : (
                      <Grid
                        templateColumns="repeat(auto-fill, minmax(240px, 1fr))"
                        gap={6}
                      >
                        {selectedCampaign.adverts.map(
                          (advert: any, index: any) => (
                            <AdvertCard
                              key={index}
                              advert={advert}
                              onEdit={() => handleEditAdvert(advert, index)}
                              onDelete={() => handleDeleteAdvert(index)}
                            />
                          )
                        )}
                      </Grid>
                    )}
                  </Stack>
                </Box>
              </Stack>
            )}
          </Box>
        </Box>
      </Flex>

      {/* Advert Form Modal */}
      <AdvertForm
        isOpen={isAdvertFormOpen}
        onClose={onAdvertFormClose}
        onSubmit={handleAdvertSubmit}
        initialValues={editingAdvert}
        toast={toast}
        restaurants={restaurants}
        disabledRestaurantIds={disabledRestaurantIds}
      />

      {/* New Campaign Modal */}
      <NewCampaignModal
        isOpen={isNewCampaignOpen}
        onClose={onNewCampaignClose}
        onCreate={handleCreateCampaign}
      />
    </div>
  )
}

const AdvertCampaignPage = () => {
  return (
    <>
      <VStack top={5} position="relative" key={"vStack"}>
        <Flex w="92%" alignItems="center" justifyContent="space-between">
          <Heading ml="8" size="md" fontWeight="semibold" color="black.400">
            Advert management
          </Heading>
          <Spacer></Spacer>
          <Heading size="sm" fontWeight="semibold">
            <Flex alignItems="center" gap={2}>
              <Image
                borderRadius="full"
                boxSize="50px"
                src="https://bit.ly/dan-abramov"
                alt="Dan Abramov"
              />
              <Text>Admin</Text>
            </Flex>
          </Heading>
        </Flex>
      </VStack>
      <AdvertCampaign />
    </>
  )
}

export default AdvertCampaignPage
